import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import DatalistInput from './DatalistInput';
import PaymentHistoryEditor from './PaymentHistoryEditor';
import SubServerEditor from './SubServerEditor';
import {
    API_BASE,
    authHeaders,
    emptyPaymentRow,
    emptySubServerRow,
    inputClassName,
    toDateInputValue,
} from '../../utils/assetHelpers';

const emptyForm = {
    mainServerName: '',
    serverType: '',
    status: '',
    loginID: '',
    loginPass: '',
    needPayment: false,
    paymentType: '',
    paymentCard: '',
    cardExpiryMonth: '',
    cardExpiryYear: '',
    remarks: '',
    paymentHistory: [],
    subServerObject: [],
};

const ServerAssetModal = ({ mode = 'create', asset = null, meta = {}, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        ...emptyForm,
        paymentHistory: [emptyPaymentRow()],
        subServerObject: [emptySubServerRow()],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const statuses = meta.statuses || [];
    const serverTypes = meta.server?.serverTypes || [];
    const paymentTypes = meta.server?.paymentTypes || [];
    const paymentCards = meta.server?.paymentCards || [];
    const currencyOptions = meta.server?.currencies || [];
    const subServerTypes = meta.server?.subServerTypes || [];
    const estCurrencies = meta.server?.estMonthlyPaymentCurrencies || [];
    const domainAssets = meta.domainAssets || [];

    useEffect(() => {
        if (mode === 'edit' && asset) {
            setFormData({
                mainServerName: asset.mainServerName || '',
                serverType: asset.serverType || '',
                status: asset.status || '',
                loginID: asset.loginID || '',
                loginPass: asset.loginPass || '',
                needPayment: Boolean(asset.needPayment),
                paymentType: asset.paymentObject?.paymentType || '',
                paymentCard: asset.paymentObject?.paymentCard || '',
                cardExpiryMonth: asset.paymentObject?.cardExpiry?.month || '',
                cardExpiryYear: asset.paymentObject?.cardExpiry?.year || '',
                remarks: asset.remarks || '',
                paymentHistory: asset.paymentHistory?.length
                    ? asset.paymentHistory.map((row) => ({
                        currency: row.currency || '',
                        amount: row.amount ?? '',
                        paymentDate: toDateInputValue(row.paymentDate),
                    }))
                    : [emptyPaymentRow()],
                subServerObject: asset.subServerObject?.length
                    ? asset.subServerObject.map((row) => ({
                        name: row.name || '',
                        type: row.type || '',
                        status: row.status || '',
                        loginID: row.loginID || '',
                        loginPass: row.loginPass || '',
                        estMonthlyPaymentCurrency: row.estMonthlyPaymentCurrency || '',
                        estMonthlyPaymentAmount: row.estMonthlyPaymentAmount ?? '',
                        domains: (row.domains || []).map((d) =>
                            (typeof d === 'object' && d?._id ? d._id : d)
                        ),
                    }))
                    : [emptySubServerRow()],
            });
        }
    }, [mode, asset]);

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                mainServerName: formData.mainServerName.trim(),
                serverType: formData.serverType.trim(),
                status: formData.status.trim(),
                loginID: formData.loginID.trim(),
                loginPass: formData.loginPass,
                needPayment: formData.needPayment,
                paymentObject: {
                    paymentType: formData.paymentType.trim().toLowerCase(),
                    paymentCard: formData.paymentCard.trim(),
                    cardExpiry: {
                        month: formData.cardExpiryMonth.trim(),
                        year: formData.cardExpiryYear.trim(),
                    },
                },
                paymentHistory: formData.paymentHistory
                    .map((row) => ({
                        currency: row.currency.trim(),
                        amount: row.amount === '' ? 0 : Number(row.amount),
                        paymentDate: row.paymentDate || null,
                    }))
                    .filter((row) => row.currency || row.amount || row.paymentDate),
                remarks: formData.remarks,
                subServerObject: formData.subServerObject
                    .map((row) => ({
                        name: row.name.trim(),
                        type: row.type.trim(),
                        status: row.status.trim(),
                        loginID: row.loginID.trim(),
                        loginPass: row.loginPass,
                        estMonthlyPaymentCurrency: row.estMonthlyPaymentCurrency.trim(),
                        estMonthlyPaymentAmount:
                            row.estMonthlyPaymentAmount === '' ? 0 : Number(row.estMonthlyPaymentAmount),
                        domains: row.domains || [],
                    }))
                    .filter((row) =>
                        row.name || row.type || row.status || row.loginID || row.domains.length
                    ),
            };

            const request = mode === 'edit'
                ? axios.put(`${API_BASE}/api/assets/servers/${asset._id}`, payload, { headers: authHeaders() })
                : axios.post(`${API_BASE}/api/assets/servers`, payload, { headers: authHeaders() });

            const response = await request;
            if (response.data.success) onSuccess(response.data.data);
        } catch (err) {
            setError(`Failed to ${mode} server: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col min-h-0 shadow-lg">
                <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-200 shrink-0">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Server Asset' : 'Create Server Asset'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
                        {error && <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Main Server Name</label>
                            <input
                                type="text"
                                required
                                value={formData.mainServerName}
                                onChange={(e) => updateField('mainServerName', e.target.value)}
                                className={inputClassName}
                            />
                        </div>
                        <DatalistInput
                            id="server-type-list"
                            label="Server Type"
                            value={formData.serverType}
                            onChange={(value) => updateField('serverType', value)}
                            options={serverTypes}
                        />
                        <DatalistInput
                            id="server-status-list"
                            label="Status"
                            value={formData.status}
                            onChange={(value) => updateField('status', value)}
                            options={statuses}
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Login ID</label>
                            <input
                                type="text"
                                value={formData.loginID}
                                onChange={(e) => updateField('loginID', e.target.value)}
                                className={inputClassName}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Login Password</label>
                            <input
                                type="text"
                                value={formData.loginPass}
                                onChange={(e) => updateField('loginPass', e.target.value)}
                                className={inputClassName}
                            />
                        </div>
                    </div>

                    <div className="border border-gray-200 rounded-md p-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800">Payment</h3>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={formData.needPayment}
                                    onChange={(e) => updateField('needPayment', e.target.checked)}
                                    className="rounded border-gray-300"
                                />
                                Need Payment
                            </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <DatalistInput
                                id="server-payment-type-list"
                                label="Payment Type"
                                value={formData.paymentType}
                                onChange={(value) => updateField('paymentType', value)}
                                options={paymentTypes}
                            />
                            <DatalistInput
                                id="server-payment-card-list"
                                label="Payment Card"
                                value={formData.paymentCard}
                                onChange={(value) => updateField('paymentCard', value)}
                                options={paymentCards}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Card Expiry (MM/YYYY)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="MM"
                                        value={formData.cardExpiryMonth}
                                        onChange={(e) => updateField('cardExpiryMonth', e.target.value)}
                                        className={inputClassName}
                                    />
                                    <input
                                        type="text"
                                        placeholder="YYYY"
                                        value={formData.cardExpiryYear}
                                        onChange={(e) => updateField('cardExpiryYear', e.target.value)}
                                        className={inputClassName}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <PaymentHistoryEditor
                        key={`${mode}-${asset?._id || 'new'}`}
                        rows={formData.paymentHistory}
                        onChange={(rows) => updateField('paymentHistory', rows)}
                        currencyOptions={currencyOptions}
                    />

                    <SubServerEditor
                        key={`${mode}-${asset?._id || 'new'}-subservers`}
                        rows={formData.subServerObject}
                        onChange={(rows) => updateField('subServerObject', rows)}
                        statuses={statuses}
                        subServerTypes={subServerTypes}
                        estCurrencies={estCurrencies}
                        domainAssets={domainAssets}
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                        <textarea
                            value={formData.remarks}
                            onChange={(e) => updateField('remarks', e.target.value)}
                            rows={3}
                            className={`${inputClassName} font-normal text-gray-800`}
                        />
                    </div>
                    </div>

                    <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
                        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update Server' : 'Create Server'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ServerAssetModal;
